import axios from "axios";
import axiosInstance from "../../helpers/bearerAxios";
import { logger } from "../loggingService";

// Base URL for the OneButton Design API, retrieved from environment variables
const baseUrl = import.meta.env.VITE_ONEBUTTON_MASTER_API_URL || '';

const cascadingDropdownsUrl = import.meta.env.VITE_ONEBUTTON_CASCADING_DROPDOWNSAPI_URL

const GetMasterDataByStep = async (payload: any) => {
    try {        
        const url = `${baseUrl}/GetMasterDataByStep`;        
        const response = await axiosInstance.post(url, payload);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const GetCompanies = async (state: any, financePartnerId?: any) => {
    try {
        // financePartnerId is optional: omit it to get every company for the state.
        const partnerQuery =
            financePartnerId == null || financePartnerId === ''
                ? ''
                : `&financePartnerId=${financePartnerId}`;
        const url = `${baseUrl}/GetCompanies?state=${state}${partnerQuery}`;
        const response = await axiosInstance.get(url);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const cascadingDropDown = async (payload: any) => {
    const token = await getExcelServiceAPIToken();
    return await axios({
        method: 'post',
        url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
        data: payload,
        headers: { Authorization: `Bearer ${token}` }
    })
        .then((res) => {
            return res.data.OutputValues;
        })
        .catch((err) => {
            logger.error(err);
            return null
        })
};

/** Parse pipe-delimited cell from CascadingDropdowns OutputValues */
function parseCascadingCellValues(outputValues: any[] | null | undefined, index = 0): string[] {
    if (!outputValues?.length) return [];
    const cell = outputValues[index];
    const v = cell?.CellValue ?? cell?.cellValue;
    if (v == null || String(v).trim() === '') return [];
    return String(v)
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * SVA Financing Options — same as oneBUTTON product-section (B4=state, C4=product code S|B, D4=partner display name).
 */
const fetchSvaFinancingMethodOptions = async (
    stateCode: string,
    productNotationC4: string,
    partnerDisplayNameD4: string
): Promise<string[]> => {
    try {
        const token = await getExcelServiceAPIToken();
        if (!token || typeof token === 'object') return [];
        const res = await axios({
            method: 'post',
            url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
            data: {
                ExcelFileName: 'Sunnova Financial Data',
                SheetName: 'SVA Financing Options',
                InputValues: [
                    { Type: 'String', CellNumber: 'B4', CellValue: String(stateCode || 'CT').trim() },
                    { Type: 'String', CellNumber: 'C4', CellValue: String(productNotationC4 || 'S').trim() },
                    { Type: 'String', CellNumber: 'D4', CellValue: String(partnerDisplayNameD4 || '').trim() },
                ],
                OutputCells: ['E4'],
            },
            headers: { Authorization: `Bearer ${token}` },
        });
        return parseCascadingCellValues(res.data?.OutputValues, 0);
    } catch (err) {
        logger.error(err);
        return [];
    }
};

/**
 * Roof pricing sheet — methods for roof / RR / RRR (C3=state, C4=product code, R3=partner).
 */
const fetchRoofFinancingMethodOptions = async (
    stateCode: string,
    roofProductCodeC4: string,
    partnerNameR3: string
): Promise<string[]> => {
    try {
        const token = await getExcelServiceAPIToken();
        if (!token || typeof token === 'object') return [];
        const res = await axios({
            method: 'post',
            url: `${import.meta.env.VITE_EXCELSERVICES_API_URL}`,
            data: {
                ExcelFileName: 'Roofing Calculator',
                SheetName: 'Roof Pricing',
                InputValues: [
                    { Type: 'String', CellNumber: 'C3', CellValue: String(stateCode || 'CT').trim() },
                    { Type: 'String', CellNumber: 'C4', CellValue: String(roofProductCodeC4 || 'R').trim() },
                    { Type: 'String', CellNumber: 'R3', CellValue: String(partnerNameR3 || '').trim() },
                ],
                OutputCells: ['T4'],
            },
            headers: { Authorization: `Bearer ${token}` },
        });
        let list = parseCascadingCellValues(res.data?.OutputValues, 0);
        const p = String(partnerNameR3 || '').toLowerCase();
        if (!p.includes('service') && !p.includes('good leap')) {
            list = list.filter((m) => String(m).trim().toLowerCase() !== 'loan');
        }
        return list;
    } catch (err) {
        logger.error(err);
        return [];
    }
};

const getExcelServiceAPIToken = async () => {
    try {
        const res = await axiosInstance.get(import.meta.env.VITE_EXCELSERVICES_TOKEN_API_URL!)
        return res.data.token;
    } catch (err) {
        return err
    }
}


const masterServices = {
    GetMasterDataByStep,
    GetCompanies,
    cascadingDropDown,
    fetchSvaFinancingMethodOptions,
    fetchRoofFinancingMethodOptions,
};

export default masterServices;